import { Box, Button, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import Table, { ColumnType } from "components/Table";
import useTitle from "hooks/useTitle";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { ResType } from "types";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetFilters } from "redux/reducers/taskboardSlice";
import { Visibility } from "@mui/icons-material";
import { getExcelFyiSections } from "api/services/automation/income-tax";
import { clientCategories, eProFyiCaseStaus, getAutFinancialYears } from "pages/atom-pro/constants";
import SearchContainer from "components/SearchContainer";
import Loader from "components/Loader";
import DateRange from "views/dashboard/OrgDashboard/DateRange";
import { getTanTempNoticeFyi } from "api/services/automation/income-tax-tan";

const ExcelProceedingFyi = (tab) => {
  const [active, setActive] = useState("FYA");
  useTitle("ATOM Pro | Excel Overview");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const dispatch = useDispatch();
  const [section, setSection] = useState<any>(null);
  const [assessmentYear, setAssessmentYear] = useState<any>(null);
  const [clientCategory, setClientCategory] = useState<any>(null);
  const [sections, setSectionData] = useState<any>(null);
  const [caseStatus, setCaseStatus] = useState<any>(null);
  const location = useLocation();
  const state: any = location.state;
  const [dates, setDates] = useState({ fromDate: null, toDate: null });

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, []);

  const { data, isLoading }: ResType = useQuery(
    [
      "tan-e-proceedings-fyi-excel",
      {
        search: search,
        limit: pageCount,
        offset: page * pageCount,
        section: section,
        assessmentYear: assessmentYear,
        clientCategory: clientCategory,
        caseStatus: caseStatus,
        ...dates,
      },
    ],
    getTanTempNoticeFyi,
    {
      onSuccess: (res) => {
        setTotalCount(res?.data?.count);
      },
    }
  );

  const { data: sectionData, isLoading: sectionIsloading }: ResType = useQuery(
    ["e-proceedings-sections-excel"],
    getExcelFyiSections,
    {
      onSuccess: (res) => {
        setSectionData(res?.data);
      },
    }
  );

  const onChange = (e: any) => {
    setSection(e.target.value);
    setPage(0);
  };

  const onStatusChange = (e: any) => {
    setCaseStatus(e.target.value);
    setPage(0);
  };

  const onAssessmentYearChange = (e: any) => {
    setAssessmentYear(e.target.value);
    setPage(0);
  };

  const onClientCategoryChange = (e: any) => {
    setClientCategory(e.target.value);
  };

  const handleSearch = (e) => {
    setSearch(e);
    setPage(0);
  };

  if (isLoading || sectionIsloading) return <Loader />;

  return (
    <>
      <Box m={1}>
        <Box mt={1}>
          <Box mb={1} display="flex" alignContent="center">
            <TextField
              name="assessmentYear"
              value={assessmentYear}
              onChange={onAssessmentYearChange}
              size="small"
              label="Assessment Year"
              select
              sx={{ width: "150px", marginRight: "20px" }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              <MenuItem key={""} value={"null"}>
                NA
              </MenuItem>
              {getAutFinancialYears().map((year: string) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              name="clientCategory"
              value={clientCategory}
              onChange={onClientCategoryChange}
              size="small"
              label="Category"
              select
              sx={{ width: "230px", marginRight: "20px" }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              {clientCategories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              name="section"
              value={section}
              onChange={onChange}
              size="small"
              label="Section"
              select
              sx={{ width: "100px", marginRight: "20px" }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              <MenuItem key={""} value={"null"}>
                NA
              </MenuItem>
              {sections?.map((option: any, index: any) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              name="caseStatus"
              value={caseStatus}
              onChange={onStatusChange}
              size="small"
              label="Case Status"
              select
              sx={{ width: "100px", marginRight: "20px" }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              {eProFyiCaseStaus?.map((option: any, index: any) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <SearchContainer
              debounced
              value={search}
              placeHolder="Search by Client | TAN | Proceeding Name"
              onChange={handleSearch}
            />
            <Box ml={1}>
              <DateRange dates={dates} setDates={setDates} setPage={setPage} />
            </Box>
            <Button
              sx={{ marginLeft: 2 }}
              onClick={() => {
                setSection("");
                setAssessmentYear(null);
                setClientCategory(null);
                setSearch("");
                setDates({ fromDate: null, toDate: null });
              }}
              size="small"
              color="secondary"
              variant="outlined"
            >
              Clear Filters
            </Button>
          </Box>
          <Table
            sx={{ pb: 0, mb: 0, height: "475px" }}
            pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
            data={data?.data.result || []}
            columns={columns}
            loading={isLoading}
            // onRowClick={handleRowClick}
          />
        </Box>
      </Box>
    </>
  );
};

const getCategoryBackgroundColor = (category) => {
  switch (category) {
    case "individual":
      return "#FCF4C5";
    case "huf":
      return "#E0F7FA";
    case "partnership_firm":
      return "#FFE0B2";
    case "llp":
      return "#D1C4E9";
    case "company":
      return "#C8E6C9";
    case "aop":
      return "#FFCCBC";
    case "boi":
      return "#C5CAE9";
    case "trust":
      return "#F8BBD0";
    case "government":
      return "#B2DFDB";
    case "local_authority":
      return "#FFD54F";
    case "artificial_judicial_person":
      return "#F0F4C3";
    case "corporations":
      return "#D7CCC8";
    default:
      return "#FFFFFF";
  }
};

const columns: Array<ColumnType> = [
  {
    key: "assesmentYear",
    title: "AY",
    render: (v) => <Typography width={"65px"}>{v?.ay === "0-01" ? "NA" : v?.ay}</Typography>,
  },
  {
    key: "",
    title: "Notice Sent Date",
    render: (v) => (
      <Typography sx={{ width: "90px" }}>{v?.noticeSentDate ? v.noticeSentDate : "--"}</Typography>
    ),
  },
  {
    key: "type",
    title: "Type",
  },
  {
    key: "",
    title: (
      <Box display="flex" alignItems="center">
        Category
      </Box>
    ),
    render: (v) => {
      const categoryLabel =
        clientCategories.find((category) => category.value === v?.client?.category)?.label || "";
      const backgroundColor = getCategoryBackgroundColor(v?.client?.category);

      return <Typography>{categoryLabel}</Typography>;
    },
  },

  {
    key: "",
    title: (
      <Box display="flex" alignItems="center">
        Client Name (TAN)
      </Box>
    ),
    render: (v) => (
      <Typography>
        {v?.client?.displayName} (<span>{v?.client?.tanClientCredentials[0]?.tanNumber}</span>)
      </Typography>
    ),
  },
  {
    key: "",
    title: (
      <Box display="flex" alignItems="center">
        Assessee PAN/TAN
      </Box>
    ),
    render: (v) => <Typography>{v?.type === "OTHER" ? v?.pan : "--"}</Typography>,
  },
  {
    key: "proceedingName",
    title: (
      <Box display="flex" alignItems="center">
        Proceeding Name
      </Box>
    ),
    render: (v) => <Typography>{v?.proceedingName ? v?.proceedingName : "--"}</Typography>,
  },
  {
    key: "",
    title: "Section",
    render: (v) => <Typography>{v?.noticeSection ? v.noticeSection : "--"}</Typography>,
  },
  {
    key: "noticeDin",
    title: "Notice DIN",
    render: (v) => <Typography>{v?.noticeDin ? v?.noticeDin : "--"}</Typography>,
  },

  {
    key: "",
    title: "Compliance Date",
    render: (v) => <Typography>{v?.dateOfCompliance ? v.dateOfCompliance : "--"}</Typography>,
  },
  {
    key: "",
    title: "Response Submitted",
    render: (v) => (
      <Typography>{v?.dateResponseSubmitted ? v.dateResponseSubmitted : "--"}</Typography>
    ),
  },
  {
    key: "proceedingStatus",
    title: "Proceeding Status",
    render: (v) => <Typography>{v?.proceedingStatus ? v.proceedingStatus : "--"}</Typography>,
  },
  {
    key: "proceedingLimitationDate",
    title: "Limitation Date",
    render: (v) => (
      <Typography>{v?.proceedingLimitationDate ? v.proceedingLimitationDate : "--"}</Typography>
    ),
  },
];
function Actions(props: any) {
  const navigate = useNavigate();

  const handleViewMoreClick = () => {
    navigate(`/atom-pro/income-tax/proceeding-information/${props.data?.id}`);
  };

  return (
    <IconButton onClick={handleViewMoreClick} size="small">
      <Visibility />
    </IconButton>
  );
}

export default ExcelProceedingFyi;
